import { makeStyles } from '@fluentui/react-components';
import { GridView, Search, ViewList } from '@material-symbols-svg/react/rounded/w500';

import { type ViewMode } from '../../preferences/models';
import { palette, tokens } from '../../theme';
import { FilterPillButton, FilterPillGroup } from './FilterPill';

export type FilterTab<K extends string> = {
    key: K;
    label: string;
    count: number;
};

export type FilterToolbarProps<K extends string> = {
    tabs: ReadonlyArray<FilterTab<K>>;
    activeTab: K;
    onTabChange: (key: K) => void;

    searchValue: string;
    onSearchChange: (next: string) => void;
    searchPlaceholder: string;
    searchAriaLabel: string;

    viewMode: ViewMode;
    onViewModeChange: (mode: ViewMode) => void;
};

export const FilterToolbar = <K extends string>({
    tabs,
    activeTab,
    onTabChange,
    searchValue,
    onSearchChange,
    searchPlaceholder,
    searchAriaLabel,
    viewMode,
    onViewModeChange,
}: FilterToolbarProps<K>): React.ReactElement => {
    const classes = useStyles();

    return (
        <section className={classes.root}>
            <FilterPillGroup>
                {tabs.map((t) => (
                    <FilterPillButton key={t.key} active={t.key === activeTab} onClick={() => onTabChange(t.key)}>
                        {t.label} ({t.count})
                    </FilterPillButton>
                ))}
            </FilterPillGroup>

            <label className={classes.searchWrap}>
                <Search className={classes.searchIcon} aria-hidden />
                <input
                    type="text"
                    className={classes.searchInput}
                    value={searchValue}
                    placeholder={searchPlaceholder}
                    onChange={(e) => onSearchChange(e.target.value)}
                    aria-label={searchAriaLabel}
                />
            </label>

            <FilterPillGroup>
                <FilterPillButton active={viewMode === 'grid'} onClick={() => onViewModeChange('grid')}>
                    <GridView className={classes.icon} aria-hidden />
                    <span>Grid</span>
                </FilterPillButton>
                <FilterPillButton active={viewMode === 'list'} onClick={() => onViewModeChange('list')}>
                    <ViewList className={classes.icon} aria-hidden />
                    <span>List</span>
                </FilterPillButton>
            </FilterPillGroup>
        </section>
    );
};

const useStyles = makeStyles({
    root: {
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalM,
        flexWrap: 'wrap',
    },
    searchWrap: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        height: '40px',
        padding: '0 18px',
        backgroundColor: palette.panelBg,
        border: `1px solid ${palette.cardBorder}`,
        borderRadius: palette.pillRadius,
        boxShadow: palette.softShadowSm,
        flexGrow: 1,
        flexShrink: 1,
        maxWidth: '340px',
        minWidth: '180px',
        marginLeft: 'auto',
        cursor: 'text',
        transition: 'border-color 160ms ease, box-shadow 160ms ease',
        '&:hover': {
            border: `1px solid ${palette.outlineGray}`,
        },
        '&:focus-within': {
            border: `1px solid ${palette.accent}`,
            boxShadow: palette.softShadowMd,
        },
    },
    searchIcon: {
        width: '18px',
        height: '18px',
        color: palette.dimText,
        flexShrink: 0,
    },
    searchInput: {
        flexGrow: 1,
        backgroundColor: 'transparent',
        border: 'none',
        outline: 'none',
        padding: 0,
        margin: 0,
        fontFamily: 'inherit',
        fontSize: '13.5px',
        color: tokens.colorNeutralForeground1,
        minWidth: 0,
        '&::placeholder': {
            color: palette.dimText,
        },
    },
    icon: {
        width: '16px',
        height: '16px',
    },
});
